/**
 * The submit check of the date picker, shared by the three netteFormsExtension.js copies (DEVDV-683).
 * An edited text field holding an incomplete, invalid or out-of-range date blocks the submit, although the
 * hidden native input behind it is valid or empty. No imports: the proxy is reached through its DOM contract.
 */

const PROXY_SELECTOR = 'input[data-date-proxy]';

/**
 * Commits every emptied, edited text field, so the "blank" triggers have run before validation.
 *
 * @param {HTMLFormElement} form
 */
export function flushDateProxies(form) {
	form.querySelectorAll(PROXY_SELECTOR).forEach((proxy) => proxy.dispatchEvent(new CustomEvent('date-proxy:flush')));
}

/**
 * @param {HTMLFormElement} form
 * @param {HTMLElement} sender what netteForms validates with: `e.submitter || form` (D19)
 * @returns {HTMLInputElement|null} the first text field that must block the submit
 */
export function findBlockingDateProxy(form, sender) {
	const scope = validationScope(sender);

	if (scope === false) {
		return null;
	}

	for (const proxy of form.querySelectorAll(PROXY_SELECTOR)) {
		if (blocks(proxy, scope)) {
			return proxy;
		}
	}

	return null;
}

/**
 * Inline mode: the picker's own error state and message, focus on the field (D22).
 * Tooltip mode (filters): the browser's bubble, never an inline message (D18).
 *
 * @param {HTMLInputElement} proxy
 */
export function reportBlockingDateProxy(proxy) {
	if (proxy.closest('smart-input[data-error-message-render=tooltip]')) {
		proxy.reportValidity();
		return;
	}

	proxy.dispatchEvent(new CustomEvent('date-proxy:report'));
	proxy.focus();
}

/**
 * @returns {RegExp|null|false} false = the sender skips validation altogether, null = the whole form
 */
function validationScope(sender) {
	if (!(sender instanceof Element) || !sender.hasAttribute('formnovalidate')) {
		return null;
	}

	const scope = JSON.parse(sender.getAttribute('data-nette-validation-scope') ?? '[]');

	return scope.length ? new RegExp('^(' + scope.join('-|') + '-)') : false;
}

function blocks(proxy, scope) {
	const native = proxy.ownerDocument.getElementById(proxy.dataset.dateProxy);

	if (!(native instanceof HTMLInputElement)) {
		throw new Error(`Date picker text field "${proxy.id}" lost its native input`);
	}

	// A field the user cannot see or fix never blocks (D15); an untouched one is judged by the server (D20).
	if (!native.willValidate || proxy.getClientRects().length === 0 || !proxy.hasAttribute('data-date-proxy-edited')) {
		return false;
	}

	if (scope && !scope.test(native.name.replace(/]\[|\[|]|$/g, '-'))) {
		return false;
	}

	return !proxy.validity.valid;
}
